// src/pages/Galerie.js
import React, { useEffect, useState, useCallback } from "react";

/**
 * Eine dynamische Bildergalerie mit Lightbox.
 * Lädt automatisch alle Ordner aus assets/galerie und zeigt sie mit Bildern an.
 * - Pfeiltasten (←/→) zum Navigieren in der Lightbox
 * - ESC schließt die Lightbox
 * - Klick auf Overlay schließt die Lightbox
 * - Voll responsive, ohne externe Abhängigkeiten
 */

export default function Galerie() {
  const [sections, setSections] = useState([]);
  const [lightboxData, setLightboxData] = useState({ isOpen: false, currentIndex: 0, images: [] });

  // Lade Bilder dynamisch
  const loadImages = useCallback(async (folderName) => {
    try {
      // Lade die manifest.json Datei, die alle Bilddateien auflistet
      const basePath = `/galerie/${folderName}`;
      const manifestResponse = await fetch(`${basePath}/manifest.json`);
      
      if (!manifestResponse.ok) {
        console.warn(`manifest.json nicht gefunden für ${folderName}`);
        return [];
      }

      const files = await manifestResponse.json();
      const images = files.map(file => ({
        src: `${basePath}/${file}`,
        alt: `${folderName} - ${file}`,
      }));

      return images;
    } catch (error) {
      console.error(`Fehler beim Laden von Bildern aus ${folderName}:`, error);
      return [];
    }
  }, []);

  // Lade einen Abschnitt (mit Text und Bildern)
  const loadSection = useCallback(async (folderName) => {
    try {
      // Lade die text.txt Datei aus dem public Ordner
      let subtitle = "";
      try {
        const textResponse = await fetch(`/galerie/${folderName}/text.txt`);
        if (textResponse.ok) {
          subtitle = await textResponse.text();
        }
      } catch (e) {
        console.warn(`text.txt nicht gefunden für ${folderName}`);
      }

      // Extrahiere die Überschrift (entferne die ersten beiden Zeichen)
      const title = folderName.substring(2);

      // Lade die Bilder dynamisch
      const images = await loadImages(folderName);

      return {
        title,
        subtitle: subtitle.trim(),
        folderName,
        images,
      };
    } catch (error) {
      console.error(`Fehler beim Laden des Abschnitts ${folderName}:`, error);
      return { title: "", subtitle: "", folderName, images: [] };
    }
  }, [loadImages]);

  // Lade alle Galerie-Daten beim Mount
  const loadGalleryData = useCallback(async () => {
    try {
      // Definiere die Ordner (in alphabetischer Reihenfolge)
      const folderNames = ["A-Hochzeit","B-Frühling & Ostern","C-Sommer","D-Herbst","E-Adventszeit","F-Dekorationen","G-Trauer"];
      const loadedSections = [];

      for (const folder of folderNames) {
        const sectionData = await loadSection(folder);
        if (sectionData.images.length > 0) {
          loadedSections.push(sectionData);
        }
      }

      setSections(loadedSections);
    } catch (error) {
      console.error("Fehler beim Laden der Galerie:", error);
    }
  }, [loadSection]);

  useEffect(() => {
    loadGalleryData();
  }, [loadGalleryData]);

  const openLightbox = (sectionIndex, imageIndex) => {
    const images = sections[sectionIndex].images;
    setLightboxData({
      isOpen: true,
      currentIndex: imageIndex,
      images,
      sectionIndex,
    });
  };

  const closeLightbox = useCallback(() => {
    setLightboxData((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const nextImage = useCallback(() => {
    setLightboxData((prev) => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length,
    }));
  }, []);

  const prevImage = useCallback(() => {
    setLightboxData((prev) => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length,
    }));
  }, []);

  // Tastatur-Shortcuts
  useEffect(() => {
    if (!lightboxData.isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxData.isOpen, closeLightbox, nextImage, prevImage]);

  return (
    <>
      <style>{css()}</style>

      {/* Alle Abschnitte */}
      {sections.map((section, sectionIndex) => (
        <section key={section.folderName} className="galerie-section">
          <div className="galerie-section__header">
            <h2 className="galerie-section__title">{section.title}</h2>
            {section.subtitle && (
              <p className="galerie-section__subtitle">{section.subtitle}</p>
            )}
          </div>

          <div className="galerie__grid">
            {section.images.map((img, imgIndex) => (
              <figure key={img.src} className="galerie__item">
                <button
                  className="galerie__thumb"
                  onClick={() => openLightbox(sectionIndex, imgIndex)}
                  aria-label={`${section.title} - Bild ${imgIndex + 1} öffnen`}
                >
                  <img src={img.src} alt={img.alt} loading="lazy" />
                </button>
              </figure>
            ))}
          </div>
        </section>
      ))}

      {/* Lightbox */}
      {lightboxData.isOpen && lightboxData.images.length > 0 && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label="Bildansicht">
          <div className="lightbox__backdrop" onClick={closeLightbox} />
          <div className="lightbox__content">
            <img
              src={lightboxData.images[lightboxData.currentIndex].src}
              alt={lightboxData.images[lightboxData.currentIndex].alt}
              className="lightbox__image"
            />

            <button className="lightbox__close" aria-label="Schließen" onClick={closeLightbox}>
              ×
            </button>
            {lightboxData.images.length > 1 && (
              <>
                <button className="lightbox__nav lightbox__nav--prev" onClick={prevImage} aria-label="Vorheriges Bild">
                  ‹
                </button>
                <button className="lightbox__nav lightbox__nav--next" onClick={nextImage} aria-label="Nächstes Bild">
                  ›
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ---------- CSS ---------- */
function css() {
  return `
:root{
  --ink:#1b1b1f; --muted:#5a5f6a; --line:#e8e9ec; --bg:#fff; --surface:#ffffff; --shadow:0 12px 40px rgba(0,0,0,.08);
  --radius:14px;
}

/* Abschnitte */
.galerie-section {
  padding: 48px 0;
  background: var(--bg);
  color: var(--ink);
}

.galerie-section__header {
  width: min(1100px, 92vw);
  margin: 0 auto 32px;
}

.galerie-section__title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 12px;
  color: var(--ink);
}

.galerie-section__subtitle {
  font-size: 1rem;
  color: var(--muted);
  margin: 0;
  line-height: 1.5;
}

/* Galerie Grid */
.galerie__grid{
  width: min(1100px, 92vw); margin: 0 auto;
  display: grid; gap: 14px;
  grid-template-columns: repeat(2, minmax(0,1fr));
}
@media (min-width: 720px){
  .galerie__grid{ grid-template-columns: repeat(3, minmax(0,1fr)); gap: 16px; }
}
@media (min-width: 1024px){
  .galerie__grid{ grid-template-columns: repeat(4, minmax(0,1fr)); gap: 18px; }
}

.galerie__item{ margin:0; }
.galerie__thumb{
  width:100%; aspect-ratio: 4 / 3; display:block; overflow:hidden;
  border: 1px solid var(--line); border-radius: 12px; background: #f8f9fc;
  padding:0; cursor: zoom-in; position: relative;
  box-shadow: 0 4px 18px rgba(0,0,0,.05);
}
.galerie__thumb img{
  width:100%; height:100%; object-fit: cover; display:block;
  transition: transform .25s ease;
}
.galerie__thumb:hover img{ transform: scale(1.03); }

/* Lightbox */
.lightbox{
  position: fixed; inset: 0; z-index: 60; display: grid; place-items: center;
}
.lightbox__backdrop{
  position: absolute; inset: 0; background: rgba(0,0,0,.5);
}
.lightbox__content{
  position: relative; z-index: 1;
  width: min(92vw, 1200px);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  padding: clamp(12px, 2vw, 16px);
  display: grid; gap: 10px;
}
.lightbox__image{
  width: 100%; height: auto; max-height: calc(100vh - 220px); object-fit: contain; border-radius: 10px;
}

/* Controls */
.lightbox__close{
  position: absolute; top: 6px; right: 10px;
  width: 36px; height: 36px; line-height: 34px; font-size: 24px;
  border-radius: 999px; border: 1px solid var(--line);
  background: #fff; cursor: pointer;
}
.lightbox__nav{
  position: absolute; top: 50%; transform: translateY(-50%);
  width: 40px; height: 40px; line-height: 36px; font-size: 28px;
  border-radius: 999px; border: 1px solid var(--line);
  background: #fff; cursor: pointer;
}
.lightbox__nav--prev{ left: 10px; }
.lightbox__nav--next{ right: 10px; }
  `;
}
